import { IProject } from "Dashboard/scenes/Projects/domain/IProject";
import { IUpdateProjectRequestParams } from "Dashboard/scenes/Projects/domain/IUpdateProjectRequestParams";
import { projectService } from "Dashboard/scenes/Projects/services/project.service";
import { useOnClickOutside } from "hooks/ClickOutside";
import { useAppDispatch } from "hooks/hooks";
import { debounce, max, values } from "lodash";
import { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { selectState, updateProject } from "../../Main/main.slice";
import { IGeneralFinancials } from "Dashboard/scenes/Projects/domain/IGeneralFinancials";
import { FormatNumber } from "utils/numberUtils";
import { IAccountReceivableFinancials } from "Dashboard/scenes/Projects/domain/IAccountReceivableFinancials";
import { IAccountPayableFinancials } from "Dashboard/scenes/Projects/domain/IAccountPayableFinancials";
import { IConsumableExemptionFinancials } from "Dashboard/scenes/Projects/domain/IConsumableExemptionFinancials";
import { ICarnetsFinancials } from "Dashboard/scenes/Projects/domain/ICarnetsFinancials";
import { IStockOutputFinancials } from "Dashboard/scenes/Projects/domain/IStockOutputFinancials";
import { IConcreteBaseFinancials } from "Dashboard/scenes/Projects/domain/IConcreteBaseFinancials";

interface IProps {
  active: boolean;
  setActive: Function;
  project: IProject;
  general: IGeneralFinancials | undefined;
  receivables: IAccountReceivableFinancials | undefined;
  payables: IAccountPayableFinancials | undefined;
  consumableExemption: IConsumableExemptionFinancials | undefined;
  carnets: ICarnetsFinancials | undefined;
  stockOutput: IStockOutputFinancials | undefined;
  concreteBaseMaterial: IStockOutputFinancials | undefined;
  concreteBaseOwnProducts: IConcreteBaseFinancials | undefined;
}

const StatisticTooltip = ({
  project,
  active,
  setActive,
  general,
  receivables,
  payables,
  consumableExemption,
  carnets,
  stockOutput,
  concreteBaseMaterial,
  concreteBaseOwnProducts,
}: IProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const { selectedIndex } = useSelector(selectState);

  const calculateExpensesAmount = (
    consumableExemption?: IConsumableExemptionFinancials,
    carnets?: ICarnetsFinancials,
    stockOutput?: IStockOutputFinancials,
    concreteBaseMaterial?: IStockOutputFinancials,
    concreteBaseOwnProducts?: IConcreteBaseFinancials,
  ): number => {
    const hourlyWorkCost = (carnets?.hourlyWorkSummaries || [])
      .reduce((sum, summary) => sum + summary.totalHoursCost, 0);
    const isConcreteBase = project?.category?.name === "Betonska baza";
    const stockCost = isConcreteBase
      ? (concreteBaseMaterial?.costAmount || 0) + (concreteBaseMaterial?.costAmountXxx || 0)
      : (stockOutput?.costAmount || 0) + (stockOutput?.costAmountXxx || 0);
    return (consumableExemption?.totalFuelEstimatedCost || 0)
      + hourlyWorkCost
      + (carnets?.totalBonusesCost || 0)
      + (carnets?.totalMealsCost || 0)
      + stockCost
      + (concreteBaseOwnProducts?.costAmountTotal || 0);
  };

  const expensesAmount = calculateExpensesAmount(
    consumableExemption, carnets, stockOutput, concreteBaseMaterial, concreteBaseOwnProducts);

  const calculateOverallBalance = (expensesAmount: number, receivables?: IAccountReceivableFinancials, payables?: IAccountPayableFinancials, general?: IGeneralFinancials): number => {
    return (receivables?.paidAmount || 0) - (payables?.paidOffAmount || 0) - expensesAmount - (general?.personalExpensesAmount || 0) - (general?.incidentsAmount || 0);
  }

  const overallBalance = calculateOverallBalance(expensesAmount, receivables, payables, general);
  const maxValue = Math.max(receivables?.paidAmount || 0,
    payables?.paidOffAmount || 0,
    expensesAmount,
    general?.personalExpensesAmount || 0,
    general?.incidentsAmount || 0,
    overallBalance);

  const calculatePercent = (value: number): number => {
    return maxValue ? Math.round((value * 100) / maxValue) : 0;
  };

  const delayedProjectUpdate = useMemo(
    () =>
      debounce(async (index: number, data: IUpdateProjectRequestParams) => {
        try {
          const project = await projectService.updateProject(data);
          dispatch(updateProject({ index, project }));
        } catch (err) {
          console.log(err);
        }
      }, 1000),
    [dispatch]
  );

  const onChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    delayedProjectUpdate(selectedIndex, {
      projectId: project.projectId,
      note: event.target.value,
    });
  };

  const hideTip = () => {
    setActive(false);
  };
  useOnClickOutside(ref, hideTip);

  if (!active) return null;

  return (
    <div className="statistic-container active" ref={ref}>
      <h1>Info</h1>
      <div className="statistic">
        <h2>{project?.projectName}</h2>
        <div>
          <span>Naplatili</span>
          <span>
            {receivables && FormatNumber(receivables.paidAmount)} rsd
          </span>
          <span
            className="chart-line green"
            style={{
              width: `${receivables && calculatePercent(receivables.paidAmount)
                }%`,
            }}
          ></span>
        </div>

        <div>
          <span>
            Isplatili<span className="details">(Saradnicima)</span>
          </span>
          <span>
            {payables && FormatNumber(payables.paidOffAmount)} rsd
          </span>
          <span
            className="chart-line blue"
            style={{
              width: `${payables && calculatePercent(payables.paidOffAmount)
                }%`,
            }}
          ></span>
        </div>

        <div>
          <span>
            Troškovi<span className="details">(Lager, gorivo, karneti)</span>
          </span>
          <span>
            {general && FormatNumber(expensesAmount)} rsd
          </span>
          <span
            className="chart-line purple"
            style={{
              width: `${general && calculatePercent(expensesAmount)
                }%`,
            }}
          ></span>
        </div>

        <div>
          <span>
            Troškovi<span className="details">(Lično)</span>
          </span>
          <span>
            {general && FormatNumber(general.personalExpensesAmount)} rsd
          </span>
          <span
            className="chart-line orange"
            style={{
              width: `${general &&
                calculatePercent(general.personalExpensesAmount)
                }%`,
            }}
          ></span>
        </div>

        <div>
          <span>Incidenti</span>
          <span>
            {general && FormatNumber(general.incidentsAmount)} rsd
          </span>
          <span
            className="chart-line pink"
            style={{
              width: `${general && calculatePercent(general.incidentsAmount)
                }%`,
            }}
          ></span>
        </div>

        <div>
          <span>Ukupan saldo</span>
          <span className="last-child">
            {general && FormatNumber(overallBalance)} rsd
          </span>
          <span
            className="chart-line red"
            style={{
              width: `${general && calculatePercent(overallBalance)
                }%`,
            }}
          ></span>
        </div>
        <textarea
          placeholder="Napomena"
          defaultValue={project?.note}
          onChange={onChange}
        ></textarea>
      </div>
    </div>
  );
};

export default StatisticTooltip;
